<template>
  <v-navigation-drawer
    v-model="isPaneOpened"
    class="documents-ai-summary-pane"
    :scrim="true"
    temporary
    persistent
    location="right"
    :width="560"
    data-locator="documents-summary-pane"
  >
    <div
      v-if="documentToSummarize"
      class="pane-wrapper"
    >
      <div class="pane-header">
        <div class="d-flex align-center">
          <v-img
            src="@/assets/img/ai_assistant_ico.svg"
            :width="28"
            :height="28"
          />
          <span class="ml-2 text--large font-weight-bold" data-locator="pane-title">
            {{ t('paneTitle') }}
          </span>
          <AppFeatureTag
            class="ml-2"
            tag="new"
            size="sm"
            style="font-family: 'Roboto', sans-serif"
          />
        </div>

        <div class="d-flex align-center ml-auto">
          <!-- Help menu waiting for protégé link to be provided -->
          <AppMenu
            v-if="false"
            data-locator="documents-summary-help-menu"
            :menu="helpMenu"
            open-on-hover
            icon-after
          >
            <template #activator="{ props }">
              <app-button size="small"
                          type="icon"
                          v-bind="props"
                >
                <app-material-icon name="help" />
              </app-button>
            </template>
          </AppMenu>
          <app-button
            data-locator="close-button"
            class="ml-2 summary-pane__close-button"
            type="icon"
            @click="closePane"
          >
            <app-material-icon name="close" />
          </app-button>
        </div>
      </div>

      <DocumentsAISummaryLoadingState
        v-if="shouldDisplayLoadingState"
        class="loading-state"
      />
      <div v-else-if="shouldDisplayLoadingError" class="loading-error">
        <p class="text--small text--regular">
          {{ t('loadingErrorText') }}
        </p>
        <app-button
          class="app-button--surface-brand"
          @click="loadSummariesForCurrentDocument"
        >
          <template #left-icon>
            <app-icon icon-name="rotate-right" icon-weight="fas" />
          </template>
          {{ $t('common.retry') }}
        </app-button>
      </div>
      <template v-else>
        <div
          class="summaries py-4"
          :class="{ 'summaries--empty': shouldDisplayEmptyState }"
        >
          <div
            v-if="shouldDisplayEmptyState"
            class="d-flex flex-column align-center justify-center"
            data-locator="empty-state"
          >
            <v-img
              src="@/assets/img/ai_assistant_logo.svg"
              :width="64"
              :height="64"
              data-locator="ai-logo"
            />
            <div class="my-6 text-center" style="max-width: 315px;">
              <p class="text--medium font-weight-bold empty-state-text" data-locator="text">
                {{ t('emptyStateText') }}
              </p>
              <div class="mt-1 text--small empty-state-description">
                {{ t('emptyStateDescription') }}
              </div>
            </div>

            <app-button
              data-locator="generate-summary-button"
              class="app-button--gradient-purple"
              :disabled="isRequestButtonDisabled"
              @click="requestDocumentSummary"
            >
              <div class="font-weight-bold">
                {{ t('generateSummary') }}
              </div>
            </app-button>
          </div>
          <template v-else>
            <DocumentsAISummaryOcrProcess
              v-if="displayOcrProcessMessage"
              class="summary"
              :status="ocrProcessStatus"
              @updateStatus="ocrProcessStatus = $event"
            />

            <DocumentsAISummaryPdfWithNoTextWarning v-if="displayPdfWithNoTextWarning" />

            <template v-for="(groupedSummaries, date) in groupedByDateSummaries" :key="date">
              <DocumentsAISummaryChatMessage
                v-for="summary in groupedSummaries"
                :key="summary.id"
                class="summary"
                :document-summary="summary"
              />
            </template>

            <DocumentsAISummaryRequestChatMessage
              v-for="requestDate in summaryRequestsDates"
              :key="requestDate"
              :request-date="requestDate"
              :document-id="documentToSummarize.id"
              class="summary"
              @error="onSummaryRequestError"
              @success="onSummaryRequestSuccess"
            />
          </template>
        </div>

        <div v-if="!shouldDisplayEmptyState && shouldDisplayActionButton" class="px-6 pt-4">
          <AppTooltip :disabled="isRequestButtonTooltipDisabled">
            <template #activator="{ props }">
              <div v-bind="props">
                <app-button
                  data-locator="generate-new-summary-button"
                  class="app-button--surface-brand"
                  :disabled="isRequestButtonDisabled"
                  full-width
                  @click="summaryRequest === 'error' ? retryDocumentSummaryRequest() : requestDocumentSummary()"
                >
                  <span class="font-weight-bold">
                    {{ t('resumeDocument') }}
                  </span>
                </app-button>
              </div>
            </template>
            <span>{{ t('noMoreCreditsRemaining') }}</span>
          </AppTooltip>
        </div>
        <AIAssistantRemainingCredits 
          :credits="summariesCredits"
          :credits-pending="summariesCreditsPending"
          class="px-6 py-4"
        />
      </template>
    </div>
  </v-navigation-drawer>
</template>

<script lang="ts">
import dayjs from 'dayjs'
import { groupBy, sortBy } from 'lodash-es'
import { defineComponent } from 'vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import AIAssistantRemainingCredits from '@/common/ai-assistant/AIAssistantRemainingCredits.vue'
import { pushToMatomo, ProtegeMatomoActions } from '@/common/analytics/matomo'
import AppFeatureTag from '@/common/AppFeatureTag.vue'
import AppMenu from '@/common/AppMenu.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import { track } from '@/common/pendo/agent'
import { DARK_BLUE_DARKEST } from '@/common/utils/colors'
import { extractOnlyDateFromDateTime } from '@/common/utils/dates'
import { useTranslation } from '@/common/utils/translation'
import DocumentsAISummaryChatMessage from '@/project/documents/ai-summary/DocumentsAISummaryChatMessage.vue'
import DocumentsAISummaryLoadingState from '@/project/documents/ai-summary/DocumentsAISummaryLoadingState.vue'
import DocumentsAISummaryOcrProcess, { DocumentAISummaryOcrStatus } from "@/project/documents/ai-summary/DocumentsAISummaryOcrProcess.vue";
import DocumentsAISummaryPdfWithNoTextWarning from '@/project/documents/ai-summary/DocumentsAISummaryPdfWithNoTextWarning.vue'
import DocumentsAISummaryRequestChatMessage from '@/project/documents/ai-summary/DocumentsAISummaryRequestChatMessage.vue'
import aiSummariesService from '@/services/ai-summaries.service'

export enum SummaryFeedback {
  FEEDBACK_POSITIVE = 1,
  FEEDBACK_NONE = 0,
  FEEDBACK_NEGATIVE = -1,
}

export type DocumentAISummary = {
  id: number
  summary: string
  feedback: SummaryFeedback
  createdAt: string
}

type DataType = {
  summariesLoading: QueryState,
  summaryRequest: QueryState,
  summaryRequestsDates: Array<string>,
  summaries: Array<DocumentAISummary>,
  DARK_BLUE_DARKEST: string,
  displayOcrProcessMessage: boolean,
  displayPdfWithNoTextWarning: boolean,
  ocrProcessStatus: DocumentAISummaryOcrStatus,
}

export default defineComponent({
  name: 'DocumentsAISummaryPane',
  emits: ['close'],
  components: {
    AIAssistantRemainingCredits,
    AppFeatureTag,
    AppMenu,
    AppTooltip,
    DocumentsAISummaryChatMessage,
    DocumentsAISummaryLoadingState,
    DocumentsAISummaryOcrProcess,
    DocumentsAISummaryPdfWithNoTextWarning,
    DocumentsAISummaryRequestChatMessage,
  },
  setup () {
    return useTranslation({ keyPrefix: 'project.documents.pane.DocumentsAISummaryPane' })
  },
  data (): DataType {
    return {
      summariesLoading: 'idle',
      summaryRequest: 'idle',
      summaryRequestsDates: [],
      summaries: [],
      DARK_BLUE_DARKEST,
      displayOcrProcessMessage: false,
      displayPdfWithNoTextWarning: false,
      ocrProcessStatus: 'available',
    }
  },
  watch: {
    async documentToSummarize (value) {
      if (value) {
        await this.loadSummariesForCurrentDocument()

        if (value.isProcessingOcr) {
          this.ocrProcessStatus = 'ocrPending'
          this.displayOcrProcessMessage = true
        }
      }
    },
    async summaryRequest () {
      if (this.isPaneOpened) {
        await this.scrollToLastSummary({ behavior: 'smooth' })
      }
    },
    isPaneOpened (value) {
      if (value) {
        this.loadSummariesCreditsForCurrentUser()
        document.body.classList.add('documents-ai-summary-pane-open')
      } else {
        document.body.classList.remove('documents-ai-summary-pane-open')
        this.displayOcrProcessMessage = false
      }
    },
  },
  computed: {
    ...mapState('documentsAISummary', [
      'documentToSummarize',
      'requestSummaryPending',
      'summariesCredits',
      'summariesCreditsPending',
    ]),
    ...mapGetters('room', ['roomMnemo']),
    groupedByDateSummaries () {
      return groupBy(
        sortBy(this.summaries, ['createdAt']),
        ({ createdAt }) => extractOnlyDateFromDateTime(createdAt),
      )
    },
    isRequestButtonDisabled () {
      return this.summaryRequest === 'pending' ||
        this.summariesCreditsPending ||
        !this.summariesCredits ||
        this.summariesCredits?.remaining === 0 ||
        (this.displayOcrProcessMessage && !this.canRetryAfterOcrProcess) ||
        this.displayPdfWithNoTextWarning
    },
    isRequestButtonTooltipDisabled () {
      return this.summariesCreditsPending || this.summariesCredits?.remaining > 0
    },
    isPaneOpened () {
      return !!this.documentToSummarize
    },
    shouldBlockPaneBeingClosed () {
      return this.isPaneOpened && this.requestSummaryPending
    },
    shouldDisplayActionButton () {
      return this.summariesLoading === 'idle'
    },
    shouldDisplayEmptyState () {
      return this.summaries.length === 0 && this.summaryRequestsDates.length === 0 && !this.displayOcrProcessMessage && !this.displayPdfWithNoTextWarning
    },
    shouldDisplayLoadingError () {
      return this.summariesLoading === 'error'
    },
    shouldDisplayLoadingState () {
      return this.summariesLoading === 'pending'
    },
    canRetryAfterOcrProcess() {
      return this.displayOcrProcessMessage && ['errorOnSummary', 'finished'].includes(this.ocrProcessStatus || '')
    },
    helpMenu () {
      return [
        {
          title: this.t('learnMoreAboutProtege'),
          icon: 'external-link',
          action: () => window.open(this.learnMoreLinkUrl, '_blank'),
        },
      ]
    },
    learnMoreLinkUrl () {
      const baseUrl = 'https://help.closd.com/'
      if (this.$i18n.locale === 'fr') {
        return `${baseUrl}fr/protege/`
      }
      return `${baseUrl}en/protege/`
    },
  },
  methods: {
    ...mapActions('documentsAISummary', ['LOAD_SUMMARIES_CREDITS']),
    ...mapMutations('documentsAISummary', ['SET_DOCUMENT_TO_SUMMARIZE_WITH_AI']),
    closePane () {
      if (this.shouldBlockPaneBeingClosed) {
        return
      }

      this.SET_DOCUMENT_TO_SUMMARIZE_WITH_AI(null)
      this.setInitialState()
    },
    async loadSummariesCreditsForCurrentUser () {
      try {
        await this.LOAD_SUMMARIES_CREDITS()

        const remaining = this.summariesCredits?.remaining
        if (typeof remaining === 'number') {
          pushToMatomo(
            'response_load',
            ProtegeMatomoActions.responseLoad,
            { credit_left: String(remaining), foldertype: 'dataroom' },
          )
        }
      } catch (error) {
        console.error(error)
      }
    },
    async loadSummariesForCurrentDocument () {
      this.summariesLoading = 'pending'

      try {
        const { data } = await aiSummariesService.getDocumentAISummaries(this.roomMnemo, this.documentToSummarize.id)
        this.summaries.push(...data)

        pushToMatomo(
          'response_load',
          ProtegeMatomoActions.responseLoad,
          { foldertype: 'dataroom' },
        )
        this.summariesLoading = 'idle'
        await this.scrollToLastSummary({ behavior: 'instant', block: 'end' })
      } catch (error) {
        console.error(error)
        this.summariesLoading = 'error'
      }
    },
    onSummaryRequestError () {
      this.summaryRequest = 'error'
    },
    async onSummaryRequestSuccess () {
      this.summaryRequest = 'idle'
      await this.loadSummariesCreditsForCurrentUser()
    },
    requestDocumentSummary () {
      if (
        import.meta.env.VUE_APP_ENABLE_OCR === 'true' &&
        this.documentToSummarize.ext === 'pdf' &&
        !this.documentToSummarize.pdfHasText &&
        !this.canRetryAfterOcrProcess
      ) {
        this.displayOcrProcessMessage = true
        return
      }

      if (import.meta.env.VUE_APP_ENABLE_OCR !== 'true' && this.documentToSummarize.ext === 'pdf' && !this.documentToSummarize.pdfHasText) {
        this.displayPdfWithNoTextWarning = true
        return
      }

      track('request_document_ai_summary')

      pushToMatomo(
        'enter_question',
        ProtegeMatomoActions.requestSummary,
        { source: 'generated summary', foldertype: 'dataroom' },
      )
      this.summaryRequestsDates.push(dayjs().toISOString())
      this.summaryRequest = 'pending'
    },
    retryDocumentSummaryRequest () {
      this.summaryRequestsDates.pop()
      this.requestDocumentSummary()
    },
    async scrollToLastSummary (scrollOptions: ScrollIntoViewOptions) {
      await this.$nextTick()
      const summariesHtmlElements = document.getElementsByClassName('summary')

      if (summariesHtmlElements.length > 0) {
        const lastSummary = summariesHtmlElements[summariesHtmlElements.length - 1]
        lastSummary.scrollIntoView(scrollOptions)
      }
    },
    setInitialState () {
      this.summariesLoading = 'idle'
      this.summaryRequest = 'idle'
      this.summaryRequestsDates = []
      this.summaries = []
      this.ocrProcessStatus = 'available'
      this.displayOcrProcessMessage = false
      this.displayPdfWithNoTextWarning = false
      this.ocrProcessStatus = 'available'
    },
  },
})
</script>

<style lang="scss">
.documents-ai-summary-pane {
  background-color: #F7F9FA;
  border-left: 1px solid #DFE3E8;
  font-family: 'Lato', sans-serif;
  line-height: 20px;
  z-index: 1020 !important; /* To be above the ChecklistPaneFiles zIndex 1018 */

  @mixin app-button-disabled {
    &:disabled {
      background-color: #EDF0F2 !important;
      color: #A0AFC0 !important;
      background-image: none !important;
    }
  }

  .app-button--gradient-purple {
    background: linear-gradient(106.69deg, #7247C9 0%, #4527A0 100%);
    color: white;
    border: none;
    min-height: 32px !important;

    &:hover:not(:disabled) {
      background: linear-gradient(106.69deg, #9675D9 0%, #4527A0 100%) !important;
    }
    @include app-button-disabled;
  }

  .app-button--surface-brand {
    background: v-bind(DARK_BLUE_DARKEST) !important;
    color: white;

    &:hover:not(:disabled) {
      background-color: #103051 !important;
    }
    &:active:not(:disabled) {
      background-color: #2A476A !important;
    }
    @include app-button-disabled;
  }

  .pane-wrapper {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .pane-header,
  .selected-document,
  .loading-state,
  .summaries {
    padding: 24px;
  }

  .pane-header {
    display: grid;
    grid-template-columns: auto 1fr auto auto;
    padding-block: 16px;
  }

  .loading-state,
  .loading-error,
  .summaries {
    flex: 1;
  }

  .summaries {
    padding-block: 4px;
    overflow-y: auto;
  }
  .summaries--empty {
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-bottom: 160px !important;
  }
  .empty-state-text {
    color: v-bind(DARK_BLUE_DARKEST);
  }
  .empty-state-description {
    color: #2A476A;
  }

  .loading-error {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
  }

  .loading-state {
    padding-top: 0;
  }

  .summary:not(:last-child) {
    margin-bottom: 24px;
  }

  .summary-pane__close-button.app-button--icon {
    width: 28px;
    height: 28px;
    min-width: 28px;
    min-height: 28px;
    background-color: #EDF0F2 !important;
    border-radius: 50%;

    &:hover:not(:disabled) {
      background-color: rgba(0, 23, 46, 0.05) !important;
    }
    &:active:not(:disabled) {
      background-color: rgba(0, 23, 46, 0.10) !important;
    }
    &:disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }
}

.documents-ai-summary-pane-open .v-tooltip .v-overlay__content {
  background-color: #00172E !important;
}
</style>
